import React, { useState, useEffect, useRef } from 'react';
import { 
  Mic, 
  MicOff, 
  Send, 
  Loader2, 
  Volume2, 
  VolumeX,
  Settings, 
  X,
  Bot,
  User,
  Sparkles,
  AlertCircle
} from 'lucide-react';
import { openaiService } from '../../services/openaiService';
import { aiAssistantService } from '../../services/aiAssistantService';
import { useAuth } from '../../hooks/useAuth';
import { pickMediaRecorderMime } from '../../utils/mediaRecorderAudio';
import type { Company, Lead, Opportunity, Task } from '../../types';

interface Message {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  data?: any;
  action?: string;
}

interface VoiceAssistantProps {
  isOpen: boolean;
  onClose: () => void;
  tasks?: Task[];
  leads?: Lead[];
  opportunities?: Opportunity[];
  companies?: Company[];
}

// Check if browser supports Web Speech API
const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
const speechSynthesis = window.speechSynthesis;

export const VoiceAssistant: React.FC<VoiceAssistantProps> = ({
  isOpen,
  onClose,
  tasks = [],
  leads = [],
  opportunities = [],
  companies = []
}) => {
  const { user } = useAuth();
  const [gpt4oAvailable, setGpt4oAvailable] = useState(false);
  const [gpt5Available, setGpt5Available] = useState(false);
  
  // Check model availability on mount
  useEffect(() => {
    const checkModels = async () => {
      try {
        const availability = await openaiService.checkModelAvailability();
        setGpt4oAvailable(availability.gpt4o);
        setGpt5Available(availability.gpt5);
        console.log('Model availability - GPT-4o:', availability.gpt4o, 'GPT-5:', availability.gpt5);
      } catch (error) {
        console.log('Could not check model availability');
      }
    };
    checkModels();
  }, []);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [useOpenAIVoice, setUseOpenAIVoice] = useState(true);
  const [voiceOutputEnabled, setVoiceOutputEnabled] = useState(() => {
    try {
      return localStorage.getItem('aideline-voice-output') === 'true';
    } catch {
      return false;
    }
  });
  const [selectedVoice, setSelectedVoice] = useState<'nova' | 'alloy' | 'echo' | 'fable' | 'onyx' | 'shimmer'>('nova');
  const [voiceQuality, setVoiceQuality] = useState<'standard' | 'premium'>('standard');
  const [voiceEmotion, setVoiceEmotion] = useState<string>('professional');
  const [showSettings, setShowSettings] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transcript, setTranscript] = useState('');
  
  const recognitionRef = useRef<any>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recorderBlobFallbackRef = useRef('audio/webm');

  // Initialize Web Speech API
  useEffect(() => {
    if (SpeechRecognition && !useOpenAIVoice) {
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onstart = () => {
        setIsListening(true);
        setError(null);
      };

      recognition.onresult = (event: any) => {
        const current = event.resultIndex;
        const transcript = event.results[current][0].transcript;
        setTranscript(transcript);
        
        if (event.results[current].isFinal) {
          setInputText(transcript);
        }
      };

      recognition.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error);
        setError(`Speech recognition error: ${event.error}`);
        setIsListening(false);
      };

      recognition.onend = () => {
        setIsListening(false);
        if (transcript) {
          handleSendMessage(transcript);
        }
      };

      recognitionRef.current = recognition;
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [useOpenAIVoice]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    try {
      localStorage.setItem('aideline-voice-output', String(voiceOutputEnabled));
    } catch {}

    if (!voiceOutputEnabled) {
      stopSpeaking();
    }
  }, [voiceOutputEnabled]);

  useEffect(() => {
    if (!isOpen) {
      stopSpeaking();
    }
  }, [isOpen]);

  // Start recording with OpenAI Whisper
  const startOpenAIRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const picked = pickMediaRecorderMime();
      recorderBlobFallbackRef.current = picked.blobFallback;

      let mediaRecorder: MediaRecorder;
      try {
        const opts: MediaRecorderOptions = picked.mimeType
          ? { mimeType: picked.mimeType, audioBitsPerSecond: 128000 }
          : { audioBitsPerSecond: 128000 };
        mediaRecorder = new MediaRecorder(stream, opts);
      } catch {
        mediaRecorder = new MediaRecorder(stream);
      }

      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        const mime = mediaRecorder.mimeType || recorderBlobFallbackRef.current;
        const audioBlob = new Blob(audioChunksRef.current, { type: mime });
        
        try {
          setIsProcessing(true);
          const transcription = await openaiService.speechToText(audioBlob);
          setInputText(transcription);
          handleSendMessage(transcription);
        } catch (error) {
          console.error('Transcription error:', error);
          setError('Failed to transcribe audio. Please try again.');
        } finally {
          setIsProcessing(false);
          stream.getTracks().forEach(track => track.stop());
        }
      };
      
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsListening(true);
      setError(null);
    } catch (error) {
      console.error('Microphone access error:', error);
      setError('Could not access microphone. Please check permissions.');
    }
  };

  // Stop recording
  const stopOpenAIRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      setIsListening(false);
    }
  };

  // Toggle listening
  const toggleListening = () => {
    if (isListening) {
      if (useOpenAIVoice) {
        stopOpenAIRecording();
      } else if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    } else {
      if (useOpenAIVoice) {
        startOpenAIRecording();
      } else if (recognitionRef.current) {
        recognitionRef.current.start();
      }
    }
  };

  // Send message to OpenAI
  const handleSendMessage = async (text: string) => {
    if (!text.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: text,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setTranscript('');
    setIsProcessing(true);
    setError(null);

    try {
      const context = {
        userId: user?.uid || '',
        orgId: user?.profile?.orgId || '',
        userName: user?.profile?.name || user?.email || 'User',
        role: user?.profile?.role
      };

      const result = await aiAssistantService.processQuery(text, context, {
        tasks,
        contacts: leads,
        opportunities,
        companies
      });

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: result.response,
        timestamp: new Date(),
        data: result.data,
        action: result.action
      };

      setMessages(prev => [...prev, assistantMessage]);

      if (voiceOutputEnabled) {
        if (useOpenAIVoice) {
          await speakWithOpenAI(result.response);
        } else {
          speakWithBrowser(result.response);
        }
      }
    } catch (error) {
      console.error('Query processing error:', error);
      
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: 'Sorry, I encountered an error processing your request. Please make sure your OpenAI API key is configured.',
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, errorMessage]);
      setError('Failed to process query. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Speak with OpenAI TTS
  const speakWithOpenAI = async (text: string) => {
    try {
      setIsSpeaking(true);
      const audioData = await openaiService.textToSpeech(text, selectedVoice, {
        quality: voiceQuality,
        emotion: voiceEmotion,
        speed: 1.0
      });
      
      if (!audioContextRef.current) {
        audioContextRef.current = new AudioContext();
      }
      
      const audioBuffer = await audioContextRef.current.decodeAudioData(audioData);
      const source = audioContextRef.current.createBufferSource();
      audioSourceRef.current = source;
      source.buffer = audioBuffer;
      source.connect(audioContextRef.current.destination);
      
      source.onended = () => {
        audioSourceRef.current = null;
        setIsSpeaking(false);
      };
      
      source.start();
    } catch (error) {
      console.error('TTS error:', error);
      // Fallback to browser TTS
      speakWithBrowser(text);
    }
  };

  // Speak with browser TTS
  const speakWithBrowser = (text: string) => {
    if (!speechSynthesis) return;

    setIsSpeaking(true);
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;
    
    utterance.onend = () => {
      setIsSpeaking(false);
    };
    
    utterance.onerror = () => {
      setIsSpeaking(false);
    };

    speechSynthesis.speak(utterance);
  };

  // Stop speaking
  const stopSpeaking = () => {
    if (audioSourceRef.current) {
      try {
        audioSourceRef.current.stop();
      } catch {}
      audioSourceRef.current = null;
    }
    if (speechSynthesis) {
      speechSynthesis.cancel();
    }
    setIsSpeaking(false);
  };

  // Example queries
  const exampleQueries = [
    "Create a task to follow up with John Smith tomorrow",
    "Show me all opportunities in negotiation stage",
    "What's our sales performance this month?",
    "Find customer information for Acme Corp",
    "List my tasks due today"
  ];

  if (!isOpen) {
    console.log('VoiceAssistant: Not open, returning null');
    return null;
  }

  console.log('VoiceAssistant: Rendering modal');
  
  // Check if API key is configured
  const hasApiKey = import.meta.env.VITE_OPENAI_API_KEY ? true : false;
  console.log('OpenAI API Key configured:', hasApiKey);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4 bg-black bg-opacity-50">
      <div className="bg-bg-card rounded-xl sm:rounded-2xl shadow-heavy w-full max-w-full sm:max-w-2xl flex flex-col h-[90vh] sm:h-[600px] max-h-[90vh] sm:max-h-[80vh]">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-default flex items-center justify-between">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className="p-1.5 sm:p-2 bg-gradient-to-br from-accent to-accent rounded-lg sm:rounded-xl">
              <Bot className="w-5 h-5 sm:w-6 sm:h-6 text-txt-inverted" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-txt">Ask Aideline</h2>
              <p className="text-xs sm:text-sm text-txt-tertiary hidden sm:block">Your AI CRM Assistant</p>
            </div>
          </div>
          <div className="flex items-center space-x-1 sm:space-x-2">
            <button
              onClick={() => setVoiceOutputEnabled(prev => !prev)}
              className={`p-1.5 sm:p-2 rounded-lg transition-colors ${
                voiceOutputEnabled
                  ? 'bg-accent-soft text-accent hover:bg-accent-soft/80'
                  : 'hover:bg-bg-surface text-txt-secondary'
              }`}
              title={voiceOutputEnabled ? 'Voice replies on' : 'Voice replies off'}
            >
              {voiceOutputEnabled ? (
                <Volume2 className="w-4 h-4 sm:w-5 sm:h-5" />
              ) : (
                <VolumeX className="w-4 h-4 sm:w-5 sm:h-5" />
              )}
            </button>
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="p-1.5 sm:p-2 hover:bg-bg-surface rounded-lg transition-colors"
            >
              <Settings className="w-4 h-4 sm:w-5 sm:h-5 text-txt-secondary" />
            </button>
            <button
              onClick={onClose}
              className="p-1.5 sm:p-2 hover:bg-bg-surface rounded-lg transition-colors"
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5 text-txt-secondary" />
            </button>
          </div>
        </div>

        {/* Settings Panel */}
        {showSettings && (
          <div className="p-3 sm:p-4 bg-bg-surface border-b border-default max-h-[50vh] overflow-y-auto">
            {/* Model Info */}
            <div className="mb-3 sm:mb-4 p-2 sm:p-3 bg-info-soft border border-accent rounded-lg">
              <div className="flex items-start space-x-2">
                <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-accent flex-shrink-0 mt-0.5" />
                <div className="text-xs sm:text-sm">
                  <p className="font-medium text-info">
                    {gpt4oAvailable ? '🚀 GPT-4o Active (PRIMARY)' : gpt5Available ? '🎉 GPT-5 Models Active' : 'OpenAI Models'} (January 2025)
                  </p>
                  <div className="mt-1 space-y-0.5 hidden sm:block">
                    {gpt4oAvailable ? (
                      <>
                        <p className="text-success">
                          • <strong>GPT-4o:</strong> Best performance, optimized latency
                        </p>
                        <p className="text-success">
                          • <strong>128K Context:</strong> Large conversation memory
                        </p>
                        <p className="text-success">
                          • <strong>Function Calling:</strong> Direct CRM integration
                        </p>
                      </>
                    ) : gpt5Available ? (
                      <>
                        <p className="text-success">
                          • <strong>GPT-5 Mini:</strong> Native audio, 256K context
                        </p>
                        <p className="text-success">
                          • <strong>Multimodal:</strong> Audio/Text in one model
                        </p>
                        <p className="text-success">
                          • <strong>o3 Reasoning:</strong> 90% less hallucinations
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-accent">
                          • <strong>Whisper:</strong> Industry-standard STT
                        </p>
                        <p className="text-accent">
                          • <strong>TTS-1-HD:</strong> High-quality voice synthesis
                        </p>
                        <p className="text-accent">
                          • <strong>GPT-4 Turbo:</strong> Advanced reasoning
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-3 sm:space-y-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-txt-secondary mb-1.5 sm:mb-2">
                  Voice Replies
                </label>
                <button
                  type="button"
                  onClick={() => setVoiceOutputEnabled(prev => !prev)}
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                    voiceOutputEnabled
                      ? 'bg-accent text-txt-inverted'
                      : 'bg-bg-card border border-default text-txt-secondary hover:bg-bg-surface'
                  }`}
                >
                  {voiceOutputEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                  {voiceOutputEnabled ? 'Spoken replies on' : 'Spoken replies off'}
                </button>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-txt-secondary mb-1.5 sm:mb-2">
                  Voice Engine
                </label>
                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      checked={useOpenAIVoice}
                      onChange={() => setUseOpenAIVoice(true)}
                      className="mr-1.5 sm:mr-2"
                    />
                    <span className="text-xs sm:text-sm">OpenAI (Better quality)</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      checked={!useOpenAIVoice}
                      onChange={() => setUseOpenAIVoice(false)}
                      className="mr-1.5 sm:mr-2"
                    />
                    <span className="text-xs sm:text-sm">Browser (Faster)</span>
                  </label>
                </div>
              </div>
              
              {useOpenAIVoice && (
                <>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-txt-secondary mb-1.5 sm:mb-2">
                      Voice Selection
                    </label>
                    <select
                      value={selectedVoice}
                      onChange={(e) => setSelectedVoice(e.target.value as any)}
                      className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-strong rounded-lg focus:ring-2 focus:ring-accent/30"
                    >
                      <option value="nova">Nova (Natural, Female)</option>
                      <option value="alloy">Alloy (Neutral)</option>
                      <option value="echo">Echo (Male)</option>
                      <option value="fable">Fable (British)</option>
                      <option value="onyx">Onyx (Deep, Male)</option>
                      <option value="shimmer">Shimmer (Soft, Female)</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-txt-secondary mb-1.5 sm:mb-2">
                      Voice Quality
                    </label>
                    <select
                      value={voiceQuality}
                      onChange={(e) => setVoiceQuality(e.target.value as 'standard' | 'premium')}
                      className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-strong rounded-lg focus:ring-2 focus:ring-accent/30"
                    >
                      <option value="standard">Standard (Fast, 0,01 €/Min)</option>
                      <option value="premium">Premium HD (Better quality)</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-txt-secondary mb-1.5 sm:mb-2">
                      Voice Emotion <span className="ml-1 px-1.5 sm:px-2 py-0.5 text-[10px] sm:text-xs bg-success-soft text-success rounded-full">NEW 2025</span>
                    </label>
                    <select
                      value={voiceEmotion}
                      onChange={(e) => setVoiceEmotion(e.target.value)}
                      className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-strong rounded-lg focus:ring-2 focus:ring-accent/30"
                    >
                      <option value="professional">Professional</option>
                      <option value="friendly">Friendly</option>
                      <option value="enthusiastic">Enthusiastic</option>
                      <option value="calm">Calm</option>
                      <option value="confident">Confident</option>
                    </select>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-6 space-y-3 sm:space-y-4 bg-bg-surface">
          {messages.length === 0 && (
            <div className="text-center py-4 sm:py-8">
              <Sparkles className="w-10 h-10 sm:w-12 sm:h-12 text-txt-muted mx-auto mb-3 sm:mb-4" />
              <p className="text-sm sm:text-base text-txt-tertiary mb-4 sm:mb-6">Ask me anything about your CRM data!</p>
              <div className="space-y-1.5 sm:space-y-2">
                <p className="text-[10px] sm:text-xs text-txt-muted uppercase tracking-wider mb-2 sm:mb-3">Try asking:</p>
                {exampleQueries.slice(0, 3).map((query, index) => (
                  <button
                    key={index}
                    onClick={() => handleSendMessage(query)}
                    className="block w-full text-left px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm text-txt-secondary bg-bg-card border border-default rounded-lg hover:bg-bg-surface transition-colors"
                  >
                    "{query}"
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`flex items-start space-x-1.5 sm:space-x-2 max-w-[90%] sm:max-w-[80%] ${message.type === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                <div className={`p-1.5 sm:p-2 rounded-lg ${message.type === 'user' ? 'bg-info' : 'bg-bg-surface-hover'}`}>
                  {message.type === 'user' ? (
                    <User className="w-3 h-3 sm:w-4 sm:h-4 text-txt-inverted" />
                  ) : (
                    <Bot className="w-3 h-3 sm:w-4 sm:h-4 text-txt-secondary" />
                  )}
                </div>
                <div className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg ${message.type === 'user' ? 'bg-info text-txt-inverted' : 'bg-bg-surface text-txt'}`}>
                  <p className="text-xs sm:text-sm">{message.content}</p>
                  {message.action && (
                    <p className="text-[10px] sm:text-xs mt-1 opacity-70">Action: {message.action}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
          
          {isProcessing && (
            <div className="flex items-center space-x-2 text-txt-tertiary">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Thinking...</span>
            </div>
          )}
          
          {transcript && isListening && (
            <div className="text-sm text-txt-tertiary italic">
              Listening: {transcript}
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Error display */}
        {error && (
          <div className="mx-3 sm:mx-6 mb-3 sm:mb-4 p-2 sm:p-3 bg-danger-soft border border-danger rounded-lg flex items-center space-x-1.5 sm:space-x-2">
            <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-danger flex-shrink-0" />
            <p className="text-xs sm:text-sm text-danger">{error}</p>
          </div>
        )}

        {/* Input Area */}
        <div className="p-3 sm:p-6 border-t border-default bg-bg-card">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <button
              onClick={toggleListening}
              disabled={isProcessing || isSpeaking}
              className={`p-2 sm:p-3 rounded-lg sm:rounded-xl transition-all ${
                isListening 
                  ? 'bg-danger hover:bg-danger text-txt-inverted animate-pulse' 
                  : 'bg-bg-surface hover:bg-bg-surface-hover text-txt-secondary'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isListening ? <MicOff className="w-4 h-4 sm:w-5 sm:h-5" /> : <Mic className="w-4 h-4 sm:w-5 sm:h-5" />}
            </button>
            
            <input
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage(inputText)}
              placeholder="Type or speak..."
              disabled={isProcessing || isListening}
              className="flex-1 px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base bg-bg-surface rounded-lg sm:rounded-xl focus:bg-bg-card focus:ring-2 focus:ring-accent/30 transition-all disabled:opacity-50"
            />
            
            {isSpeaking && (
              <button
                onClick={stopSpeaking}
                className="p-2 sm:p-3 bg-warning hover:bg-warning-soft text-txt-inverted rounded-lg sm:rounded-xl transition-all"
              >
                <Volume2 className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            )}
            
            <button
              onClick={() => handleSendMessage(inputText)}
              disabled={!inputText.trim() || isProcessing}
              className="p-2 sm:p-3 bg-info hover:bg-accent text-txt-inverted rounded-lg sm:rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? (
                <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
              ) : (
                <Send className="w-4 h-4 sm:w-5 sm:h-5" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
